/**
 * Storage Wrapper
 * Provides a global storage instance that can be used throughout the app
 * Automatically uses IndexedDB or localStorage based on configuration
 */

let storageInstance = null;

/**
 * Set the storage instance (called by initStorage)
 * @param {IStorage} storage
 */
export function setStorageInstance(storage) {
    storageInstance = storage;
}

/**
 * Get the current storage instance
 * @returns {IStorage}
 */
export function getStorageInstance() {
    if (!storageInstance) {
        throw new Error('Storage not initialized. Make sure initializeStorage() is called first.');
    }
    return storageInstance;
}

/**
 * Storage API - Drop-in replacement for localStorage
 * All methods are async and return Promises
 */
export const storage = {
    /**
     * Get item from storage
     * @param {string} key
     * @returns {Promise<any>}
     */
    async getItem(key) {
        const instance = getStorageInstance();
        return await instance.get(key);
    },

    /**
     * Set item in storage
     * @param {string} key
     * @param {any} value
     * @returns {Promise<void>}
     */
    async setItem(key, value) {
        const instance = getStorageInstance();
        return await instance.set(key, value);
    },

    /**
     * Remove item from storage
     * @param {string} key
     * @returns {Promise<void>}
     */
    async removeItem(key) {
        const instance = getStorageInstance();
        return await instance.remove(key);
    },

    /**
     * Clear all items from storage
     * @returns {Promise<void>}
     */
    async clear() {
        const instance = getStorageInstance();
        return await instance.clear();
    },

    /**
     * Get all keys from storage
     * @returns {Promise<string[]>}
     */
    async keys() {
        const instance = getStorageInstance();
        return await instance.keys();
    },

    /**
     * Check if key exists
     * @param {string} key
     * @returns {Promise<boolean>}
     */
    async has(key) {
        const instance = getStorageInstance();
        return await instance.has(key);
    },

    /**
     * Get number of items in storage
     * @returns {Promise<number>}
     */
    async length() {
        const instance = getStorageInstance();
        return await instance.length();
    }
};

export default storage;
